import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CodeIcon from '@mui/icons-material/Code';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../../api/axiosClient';
import { PageHeader, SearchField, EmptyState, TableSkeleton, WriteButton, WriteIconButton } from '../../../components/ui';
import { colors, typo, tableHeadCellSx, tableCellSx, primaryButtonSx, secondaryButtonSx, dialogActionsSx } from '../../../theme/tokens';

/**
 * R7 route-move of the former tenant-scoped Scripts page, reduced to the
 * GLOBAL admin-scripts surface only.
 *
 * What's included here:
 *   - GET    /api/admin/admin-scripts/all
 *   - POST   /api/admin/admin-scripts
 *   - DELETE /api/admin/admin-scripts/:code
 *   - (run-confirmation UI — execution is still a stub, same as legacy)
 *
 * What was intentionally DROPPED from the original page:
 *   - Task-reward-script bindings tab (tenant-scoped; stays in tenant app)
 *   - Event-handlers tab (tenant-scoped; stays in tenant app)
 *   - Import admin.json button (import/export lands under its own admin
 *     route in R7 — see /admin/import-export)
 *
 * The underlying Express router still exposes the tenant-scoped endpoints
 * under the new mount too (same Router), but this page does not surface
 * them — tenant-scoped script management is deferred to a later round.
 */

const ADMIN_SCRIPTS_BASE = '/api/admin/admin-scripts';

const AdminScriptsPage: React.FC = () => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [confirmRun, setConfirmRun] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const { data: scripts = [], isLoading: loadingScripts } = useQuery({
    queryKey: ['admin-scripts-all'],
    queryFn: () => axiosClient.get(`${ADMIN_SCRIPTS_BASE}/all`).then(r => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: (data: any) => axiosClient.post(ADMIN_SCRIPTS_BASE, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-scripts-all'] });
      setEditOpen(false);
      setSnackbar({ open: true, message: 'Saved', severity: 'success' });
    },
    onError: () => setSnackbar({ open: true, message: 'Failed to save', severity: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (code: string) => axiosClient.delete(`${ADMIN_SCRIPTS_BASE}/${code}`, { data: { created_by: 'admin' } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-scripts-all'] });
      setSnackbar({ open: true, message: 'Deleted', severity: 'success' });
    },
  });

  const openNewScript = () => { setForm({ script_code: '', script_name: '', script_description: '', script_source: '', script_json: '{}' }); setEditOpen(true); };
  const openEditScript = (s: any) => { setForm({ ...s, script_json: JSON.stringify(s.script_json || {}, null, 2) }); setEditOpen(true); };

  const handleSave = () => {
    const payload: Record<string, any> = { ...form, created_by: 'admin' };
    try { payload.script_json = JSON.parse(form.script_json || '{}'); } catch { payload.script_json = {}; }
    saveMutation.mutate(payload);
  };

  const filterFn = (item: any) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return Object.values(item).some((v: any) => typeof v === 'string' && v.toLowerCase().includes(s));
  };

  return (
    <Box>
      <PageHeader
        title="Admin Scripts"
        subtitle="Global scripts run by operators (not tenant-scoped)"
        actions={
          <WriteButton variant="contained" startIcon={<AddIcon />} onClick={openNewScript}
            sx={primaryButtonSx}>
            Add Script
          </WriteButton>
        }
      />

      <Box sx={{ mb: 3 }}>
        <SearchField value={search} onChange={setSearch} placeholder="Search scripts..." />
      </Box>

      {loadingScripts ? (
        <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${colors.borderLight}`, boxShadow: 'none' }}>
          <Table size="small">
            <TableHead><TableRow>
              {['Code', 'Name', 'Source Preview', 'Version', ''].map(h => (
                <TableCell key={h} sx={tableHeadCellSx}>{h}</TableCell>
              ))}
            </TableRow></TableHead>
            <TableBody><TableSkeleton rows={5} columns={5} /></TableBody>
          </Table>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${colors.borderLight}` }}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell sx={tableHeadCellSx}>Code</TableCell>
              <TableCell sx={tableHeadCellSx}>Name</TableCell>
              <TableCell sx={tableHeadCellSx}>Source Preview</TableCell>
              <TableCell sx={tableHeadCellSx}>Version</TableCell>
              <TableCell sx={{ ...tableHeadCellSx, width: 100 }} />
            </TableRow></TableHead>
            <TableBody>
              {scripts.filter(filterFn).map((s: any) => {
                const sourceLines = (s.script_source || '').split('\n').filter((l: string) => l.trim());
                const sourcePreview = sourceLines.slice(0, 2).join('\n');
                return (
                  <TableRow key={s.script_code} hover sx={{ cursor: 'pointer', '&:hover': { backgroundColor: colors.brandSubtle } }} onClick={() => openEditScript(s)}>
                    <TableCell sx={{ ...tableCellSx }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <CodeIcon sx={{ fontSize: 16, color: colors.infoText }} />
                        <Typography sx={{ ...typo.mono, fontSize: 12 }}>{s.script_code}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{s.script_name}</Typography>
                      {s.script_description && <Typography noWrap sx={{ ...typo.tinyText, maxWidth: 200, mt: 0.25 }}>{s.script_description}</Typography>}
                    </TableCell>
                    <TableCell sx={{ ...tableCellSx, maxWidth: 280 }}>
                      {sourcePreview ? (
                        <Paper elevation={0} sx={{ px: 1, py: 0.5, borderRadius: '6px', backgroundColor: colors.bgCode, border: `1px solid ${colors.borderLight}` }}>
                          <Typography noWrap component="pre" sx={{ ...typo.mono, fontSize: 11, color: colors.textSecondary, m: 0, whiteSpace: 'pre', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {sourcePreview}
                          </Typography>
                        </Paper>
                      ) : (
                        <Typography sx={typo.tinyText}>No source</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={tableCellSx}>v{s.version}</TableCell>
                    <TableCell sx={tableCellSx}>
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        <WriteIconButton size="small" onClick={e => { e.stopPropagation(); setConfirmRun(s.script_code); }}
                          sx={{ color: colors.successIcon, '&:hover': { backgroundColor: colors.successBg } }}>
                          <PlayArrowIcon sx={{ fontSize: 18 }} />
                        </WriteIconButton>
                        <WriteIconButton size="small" onClick={e => { e.stopPropagation(); setDeleteTarget(s.script_code); }}>
                          <DeleteIcon sx={{ fontSize: 18, color: colors.errorIcon }} />
                        </WriteIconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {scripts.filter(filterFn).length === 0 && <TableRow><TableCell colSpan={5} sx={{ p: 0 }}>
                <EmptyState variant={search ? 'search' : 'data'} title={search ? 'No scripts match your search' : 'No scripts'} description="Add a script to get started." />
              </TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Script</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Code *" size="small" value={form.script_code || ''} onChange={e => setForm({ ...form, script_code: e.target.value })} />
            <TextField label="Name *" size="small" value={form.script_name || ''} onChange={e => setForm({ ...form, script_name: e.target.value })} />
            <TextField label="Description" size="small" multiline minRows={2} value={form.script_description || ''} onChange={e => setForm({ ...form, script_description: e.target.value })} />
            <TextField label="Source (C# code)" size="small" multiline minRows={4} maxRows={12} value={form.script_source || ''}
              onChange={e => setForm({ ...form, script_source: e.target.value })} InputProps={{ sx: { ...typo.mono, backgroundColor: colors.bgCode, borderRadius: '8px' } }} />
            <TextField label="Script JSON" size="small" multiline minRows={3} value={form.script_json || '{}'}
              onChange={e => setForm({ ...form, script_json: e.target.value })} InputProps={{ sx: { ...typo.mono, backgroundColor: colors.bgCode, borderRadius: '8px' } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setEditOpen(false)} sx={secondaryButtonSx}>Cancel</Button>
          <WriteButton variant="contained" onClick={handleSave} disabled={saveMutation.isPending}
            sx={primaryButtonSx}>Save</WriteButton>
        </DialogActions>
      </Dialog>

      {/* Run Script Confirmation */}
      <Dialog open={!!confirmRun} onClose={() => setConfirmRun(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>Run Script</DialogTitle>
        <DialogContent>
          <Typography sx={typo.bodyText}>
            Are you sure you want to execute script <strong>{confirmRun}</strong>?
          </Typography>
          <Paper elevation={0} sx={{ mt: 1.5, p: 1.5, borderRadius: '8px', backgroundColor: colors.warningBg, border: `1px solid ${colors.warningBorder}` }}>
            <Typography sx={{ fontSize: 12, color: colors.warningText }}>
              This will execute the script in the current environment. Make sure you have reviewed the script source before running.
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setConfirmRun(null)} sx={secondaryButtonSx}>Cancel</Button>
          <WriteButton variant="contained"
            onClick={() => { setConfirmRun(null); setSnackbar({ open: true, message: `Script ${confirmRun} execution is available in production environments`, severity: 'success' }); }}
            sx={{ ...primaryButtonSx, backgroundColor: colors.successIcon, '&:hover': { backgroundColor: colors.successText } }}
            startIcon={<PlayArrowIcon sx={{ fontSize: 16 }} />}>
            Run
          </WriteButton>
        </DialogActions>
      </Dialog>

      {/* Delete Script Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>Delete Script</DialogTitle>
        <DialogContent>
          <Typography sx={typo.bodyText}>
            Are you sure you want to delete script <strong>{deleteTarget}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setDeleteTarget(null)} sx={secondaryButtonSx}>Cancel</Button>
          <WriteButton variant="contained"
            onClick={() => { if (deleteTarget) deleteMutation.mutate(deleteTarget); setDeleteTarget(null); }}
            sx={{ ...primaryButtonSx, backgroundColor: colors.errorIcon, '&:hover': { backgroundColor: colors.errorText } }}>
            Delete
          </WriteButton>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminScriptsPage;
